import { Modal, ModalDialog, Stack } from '@mui/joy';
import type { ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import GFormProvider from 'components/technical/form/GFormProvider';
import { GraphQLApiFormErrorMessage } from 'components/technical/form/GraphQLApiErrorMessage';
import gYupResolver from 'components/technical/form/gYupResolver';
import SubmitButton from 'components/technical/form/SubmitButton.tsx';
import { useGraphQLApiError } from 'components/technical/form/UseGraphQLApiError.tsx';
import GDialogHeader from 'components/technical/GDialog/GDialogHeader.tsx';
import { FormInput } from 'components/technical/inputs';
import {
  createSecretFields,
  createSecretFieldsSchema,
  getInitialValues,
  prepareSecretInput,
} from './createAccount/CredentialsService.ts';
import type { useUpdateAccountApiKeysMutation } from '../../../generated/graphql';
import { venues } from '../../venue/VenueData.tsx';

type FormState = Record<string, string>;

interface UpdateApiKeysDialogProps {
  onClose: () => void;
  onUpdate: () => void;
  name: string;
  venue: string;
  id: string;
  mutation: typeof useUpdateAccountApiKeysMutation;
}

const UpdateApiKeysDialog = ({
  onClose,
  onUpdate,
  name,
  venue,
  id,
  mutation,
}: UpdateApiKeysDialogProps): ReactElement => {
  const venueData = venues[venue];
  const secrets = createSecretFields(venueData);
  const defaultValues = getInitialValues(secrets);

  const methods = useForm<FormState>({
    resolver: gYupResolver(createSecretFieldsSchema(venueData)),
    defaultValues,
  });

  const { onErrorAndThrow } = useGraphQLApiError(methods);

  const [updateApiKeysMutation] = mutation();

  const handleFormSubmit = async (data: FormState): Promise<void> => {
    const secrets = prepareSecretInput(venueData, data.secrets as unknown as Record<string, string>);
    try {
      await updateApiKeysMutation({
        variables: {
          input: {
            secrets: secrets,
            id: id,
          },
        },
      });

      onUpdate();
    } catch (e) {
      onErrorAndThrow(e);
    }
  };

  const ExtraFields = venues[venue].extraFields;
  return (
    <Modal open={true} onClose={onClose}>
      <ModalDialog>
        <GFormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleFormSubmit)}>
            <GDialogHeader>Change {name} credentials</GDialogHeader>
            <Stack spacing={3}>
              <Stack spacing={1.5}>
                {secrets.map((field) => (
                  <FormInput
                    key={`input-${field.name}`}
                    type={field.type ?? 'text'}
                    label={field.label}
                    name={field.name}
                    autoComplete="off"
                    width="fullWidth"
                    showLabelAboveInput
                  />
                ))}
                {ExtraFields && <ExtraFields />}
              </Stack>
              {/* jscpd:ignore-start */}
              <Stack alignItems="center" spacing={1.5}>
                <GraphQLApiFormErrorMessage />
                <SubmitButton width="xl2">Update API keys</SubmitButton>
              </Stack>
              {/* jscpd:ignore-end */}
            </Stack>
          </form>
        </GFormProvider>
      </ModalDialog>
    </Modal>
  );
};

export default UpdateApiKeysDialog;
